import {
  getRouteLabel,
  matchRoute,
  ROUTE_METADATA,
  ROUTES,
  RouteKey,
} from '@common/constants/routes'
import {
  AtlasObjectType,
  atlasPathOf,
  fillRoutePath,
  parseAtlasObjectUrl,
  withQueryParams,
} from '@common/helpers/atlasUrls'
import { IAtlasAISuggestion } from '@common/interfaces/atlasai'
import { buildAtlasAISuggestionData } from '../../components/Common/Mentions/mentionHelpers'

/** What the voice is told back: where the screen went, or that it couldn't find it. */
export interface IGoToResult {
  opened?: string
  /** Close matches it didn't open, so the voice can offer them. */
  alsoMatched?: string[]
  notFound?: string
}

export interface IGoToDeps {
  /** Records whose name matches, as the @-mention picker finds them. */
  findRecords: (name: string) => Promise<IAtlasAISuggestion[]>
  navigate: (url: string, label: string) => void
  /** The path they're on, so a tab or sub-page of the record on screen needs no lookup. */
  currentPath?: () => string
}

type QueryValue = string | number | boolean

const OTHER_MATCHES_MAX = 2

const isPageKey = (page: unknown): page is RouteKey =>
  typeof page === 'string' && Object.hasOwn(ROUTES, page)

/** The voice's `query` argument, keeping only the values a URL can carry. */
const queryOf = (query: unknown): Record<string, QueryValue> =>
  query && typeof query === 'object'
    ? Object.fromEntries(
        Object.entries(query).filter((entry): entry is [string, QueryValue] =>
          ['string', 'number', 'boolean'].includes(typeof entry[1]),
        ),
      )
    : {}

/** A client's due diligence pages are keyed by the same client_info id as its client page. */
const SAME_ID_TYPES: AtlasObjectType[][] = [[AtlasObjectType.Client, AtlasObjectType.DueDiligence]]

const recordTypeOf = (path: string): AtlasObjectType | null =>
  parseAtlasObjectUrl(path)?.type ?? null

/** Whether a record of one type can fill the id of a page that belongs to another. */
const sharesRecordId = (recordType: AtlasObjectType | null, pageType: AtlasObjectType): boolean =>
  !recordType ||
  recordType === pageType ||
  SAME_ID_TYPES.some((group) => group.includes(recordType) && group.includes(pageType))

/** Spoken names come with filler: "Barnana's", "the Barnana page". */
const cleanRecordName = (name: string): string =>
  name
    .replace(/['’]s\b/gi, '')
    .replace(/\b(the|client|company|page|profile)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

/** The record a spoken name means: an exact name first, then the best match that has a page. */
const pickRecord = (name: string, records: IAtlasAISuggestion[]) => {
  const withLinks = records
    .map((record) => ({ record, link: buildAtlasAISuggestionData(record).link }))
    .filter((entry): entry is { record: IAtlasAISuggestion; link: string } => !!entry.link)
  const wanted = name.trim().toLowerCase()
  const exact = withLinks.find(({ record }) => record.name.trim().toLowerCase() === wanted)
  const chosen = exact ?? withLinks[0]
  const others = withLinks
    .filter((entry) => entry !== chosen)
    .slice(0, OTHER_MATCHES_MAX)
    .map(({ record }) => record.name)
  return chosen ? { ...chosen, others } : null
}

/**
 * The voice's go_to tool, run in the browser: a page goes straight to its route,
 * a record is found the way the @-mention picker finds it, and a tab or sub-page
 * of a record takes its id from the record named or the one already on screen.
 * No Arc turn sits between asking and the screen moving.
 */
export const createGoTo =
  ({ findRecords, navigate, currentPath }: IGoToDeps) =>
  async ({ page, record, query, url }: Record<string, unknown>): Promise<IGoToResult> => {
    const extraQuery = queryOf(query)
    // Any host: a result's link may carry a misconfigured one; only its path opens, here.
    const linkPath = typeof url === 'string' ? atlasPathOf(url, { anyHost: true }) : null
    if (linkPath) {
      const label = getRouteLabel(linkPath.split(/[?#]/)[0]) ?? linkPath
      navigate(withQueryParams(linkPath, extraQuery), label)
      return { opened: label }
    }
    const hasPage = isPageKey(page)
    const recordName = typeof record === 'string' ? cleanRecordName(record) : ''
    if (!hasPage && !recordName) return { notFound: 'Say which page or record to open.' }

    const match = recordName ? pickRecord(recordName, await findRecords(recordName)) : null
    if (recordName && !match) {
      return { notFound: `Nothing called "${recordName}" has a page to open.` }
    }
    const others = match?.others.length ? { alsoMatched: match.others } : {}

    if (!hasPage) {
      navigate(withQueryParams(match.link, extraQuery), match.record.name)
      return { opened: match.record.name, ...others }
    }

    // A page of a record takes its ids from the record named, else from the page they're on.
    const onScreen = matchRoute(currentPath?.() ?? '')?.params ?? {}
    const filled = fillRoutePath(page, { ...onScreen, ...(match ? { id: match.record.id } : {}) })
    const { label } = ROUTE_METADATA[page]
    if ('missing' in filled) {
      return { notFound: `${label} is a page of one record: say which, or open it first.` }
    }
    const pageType = match ? recordTypeOf(filled.path) : null
    if (match && pageType && !sharesRecordId(recordTypeOf(match.link), pageType)) {
      return {
        notFound: `${match.record.name} doesn't have a ${label}: say which record you mean.`,
      }
    }
    const opened = match ? `${match.record.name}, ${label}` : label
    navigate(withQueryParams(filled.path, extraQuery), opened)
    return { opened, ...others }
  }
